import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useCallback,
} from 'react'
import { IDentalChart } from '@dataTypes/chartingFeature.types'
import { formatProviderDisplayName } from '@utils/provider-display.utils'
import { ConfirmationModal, DataGridComponent } from '@components/common'
import { EmptyState } from '@components/ui'
import ChartingDeleteItemModal from '@components/pages/Charting/Modals/ChartingDeleteItemModal.component'
import ChartingDiscardInProgressModal from '@components/pages/Charting/Modals/ChartingDiscardInProgressModal.component'
import ChartingEditChartModal from '@components/pages/Charting/Modals/ChartingEditChartModal.component'
import {
  GridComponent,
  RowSelectEventArgs,
  ColumnModel,
  ActionEventArgs,
} from '@syncfusion/ej2-react-grids'
import type { RecordDoubleClickEventArgs } from '@syncfusion/ej2-grids'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import type { RootState } from '@store/index'
import { useChartHistoryGridSelection } from '@hooks/useChartHistoryGridSelection'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import Button from '@components/common/Button/Button.component'
import { deleteDentalChart } from '@store/actions'
import {
  setCreateDentalChart,
  setDentalUpdated,
  setPreferCreateDentalChart,
  setSelectedDentalChart,
} from '@store/reducers/charting.reducer'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createDentalChartDeletedToast,
  createFailedToDeleteChartToast,
} from '@utils/notificationToast.util'
import {
  formatDentalDataSummary,
  DentalDataSummary,
} from '@utils/dental-data-summary.utils'
import { groupDentalDataToothSurfaceColumn } from '@utils/charting-surface-display.utils'
import {
  getChartHistoryRowId,
  syncChartHistoryEditButtons,
} from '@utils/chart-history-selection.utils'
import { getAlignedDentalEditChartId } from '@utils/active-dental-chart.utils'
import DentalChartSummaryContent from './DentalChartSummaryContent.component'

interface ChartingHistoryProps {
  onViewNote: (id: string | null, openFirstNote?: boolean) => void
  onSelectHistory: (chart: RowSelectEventArgs) => void
  gridRef: React.RefObject<GridComponent>
  /** Bumps when chart layout changes (e.g. x-ray panel) to re-sync grid selection. */
  layoutSyncKey?: string
}

const ChartingHistory: React.FC<ChartingHistoryProps> = ({
  onViewNote,
  onSelectHistory,
  gridRef,
  layoutSyncKey,
}) => {
  const isMountedRef = useRef<boolean>(true)
  const store = useStore<RootState>()
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()

  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const dentalChartsRaw = useAppSelector((state) => state.charting.dentalCharts)
  const dentalCharts = useMemo(() => dentalChartsRaw ?? [], [dentalChartsRaw])
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const isDentalUpdated = useAppSelector(
    (state) => state.charting.isDentalUpdated,
  )
  const createDentalChart = useAppSelector(
    (state) => state.charting.createDentalChart,
  )
  const preferCreateDentalChart = useAppSelector(
    (state) => state.charting.preferCreateDentalChart,
  )

  const [showEditConfirm, setShowEditConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [summaryChartTitle, setSummaryChartTitle] = useState('')
  const [dentalSummary, setDentalSummary] = useState<DentalDataSummary | null>(
    null,
  )
  const [chartToEdit, setChartToEdit] = useState<IDentalChart | null>(null)
  const [chartToDelete, setChartToDelete] = useState<IDentalChart | null>(null)
  const [pendingDeleteChartId, setPendingDeleteChartId] = useState<
    string | null
  >(null)
  const [pendingDeleteChartTitle, setPendingDeleteChartTitle] =
    useState<string>('')
  const [isDeleteInFlight, setIsDeleteInFlight] = useState(false)
  const hasHandledPendingDeleteRef = useRef<boolean>(false)
  const lastDeleteSuccessToastChartIdRef = useRef<string | null>(null)
  const onViewNoteRef = useRef(onViewNote)
  onViewNoteRef.current = onViewNote
  const editingChartId = useMemo(
    (): string =>
      getAlignedDentalEditChartId(
        selectedDentalChart,
        createDentalChart,
        preferCreateDentalChart,
      ),
    [createDentalChart, preferCreateDentalChart, selectedDentalChart],
  )
  const editingChartIdRef = useRef(editingChartId)
  editingChartIdRef.current = editingChartId

  useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Additional cleanup when patient is deselected
  useEffect(() => {
    if (!selectedPatient && isMountedRef.current) {
      dispatch(setSelectedDentalChart(null))
    }
  }, [selectedPatient, dispatch])

  // Clear selectedDentalChart when patient changes and chart doesn't match
  useEffect(() => {
    if (
      selectedPatient &&
      selectedDentalChart &&
      selectedDentalChart.patient_id !== selectedPatient.patient_id
    ) {
      dispatch(setSelectedDentalChart(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient, selectedDentalChart, dispatch])

  const historyData = useMemo(() => {
    return selectedPatient && !chartingLoading
      ? dentalCharts
          .filter((entry) => entry.patient_id === selectedPatient.patient_id)
          .map((entry) => {
            return {
              ...entry,
              provider_name: formatProviderDisplayName(entry.provider),
              tooth_surface: groupDentalDataToothSurfaceColumn(
                entry.dental_data,
              ),
              date: entry.date_time_created_at,
            }
          })
      : []
  }, [selectedPatient, chartingLoading, dentalCharts])

  const shouldIgnoreDeselect = useCallback(
    (args: RowSelectEventArgs): boolean => {
      const deselectedChartId = getChartHistoryRowId(args.data)
      return Boolean(
        isDeleteInFlight &&
          pendingDeleteChartId &&
          deselectedChartId === pendingDeleteChartId,
      )
    },
    [isDeleteInFlight, pendingDeleteChartId],
  )

  const {
    suppressSelectionSyncRef,
    selectChartRowIfAvailable,
    handleRowSelected,
    handleRowDeselected,
    handleRecordClick,
    handleRowDataBound,
    handleDataBound,
    handleGridActionComplete: syncHistorySelectionAfterGridAction,
  } = useChartHistoryGridSelection({
    gridRef,
    historyData,
    selectedChartId: selectedDentalChart?.chart_id?.trim() ?? '',
    onSelectHistory,
    isMountedRef,
    canSyncSelection: Boolean(selectedPatient),
    shouldIgnoreDeselect,
    getSelectedChartId: (): string =>
      store.getState().charting.selectedDentalChart?.chart_id?.trim() ?? '',
  })

  const handleHistoryDataBound = useCallback((): void => {
    handleDataBound()
    syncChartHistoryEditButtons(gridRef.current, editingChartId)
  }, [editingChartId, gridRef, handleDataBound])

  const clearPendingDeleteState = useCallback((): void => {
    setPendingDeleteChartId(null)
    setPendingDeleteChartTitle('')
    setIsDeleteInFlight(false)
    hasHandledPendingDeleteRef.current = false
  }, [])

  const reconcilePendingDelete = useCallback((): void => {
    if (!pendingDeleteChartId || isDeleteInFlight) return
    if (hasHandledPendingDeleteRef.current) return

    const wasDeleted = !dentalCharts.some(
      (chart) => chart.chart_id === pendingDeleteChartId,
    )
    if (!wasDeleted) return
    hasHandledPendingDeleteRef.current = true

    if (lastDeleteSuccessToastChartIdRef.current === pendingDeleteChartId) {
      clearPendingDeleteState()
      return
    }

    if (selectedDentalChart?.chart_id === pendingDeleteChartId) {
      dispatch(setSelectedDentalChart(null))
    }

    if (gridRef.current?.element?.parentNode) {
      const visibleRows =
        (gridRef.current.getCurrentViewRecords?.() as
          | IDentalChart[]
          | undefined) ?? []
      if (visibleRows.length > 0) {
        selectChartRowIfAvailable(visibleRows[0].chart_id)
      }
    }

    showToast(createDentalChartDeletedToast(pendingDeleteChartTitle || 'chart'))
    lastDeleteSuccessToastChartIdRef.current = pendingDeleteChartId

    clearPendingDeleteState()
  }, [
    clearPendingDeleteState,
    dentalCharts,
    dispatch,
    gridRef,
    isDeleteInFlight,
    pendingDeleteChartId,
    pendingDeleteChartTitle,
    selectChartRowIfAvailable,
    selectedDentalChart?.chart_id,
    showToast,
  ])

  const handleGridActionComplete = useCallback(
    (args: ActionEventArgs): void => {
      const requestType = String(args.requestType ?? '')
      const shouldSyncSelection =
        requestType === 'refresh' ||
        requestType === 'paging' ||
        requestType === 'sorting' ||
        requestType === 'filtering'
      if (pendingDeleteChartId && !isDeleteInFlight && shouldSyncSelection) {
        reconcilePendingDelete()
      }
      syncHistorySelectionAfterGridAction(args)
      syncChartHistoryEditButtons(gridRef.current, editingChartId)
    },
    [
      editingChartId,
      gridRef,
      isDeleteInFlight,
      pendingDeleteChartId,
      reconcilePendingDelete,
      syncHistorySelectionAfterGridAction,
    ],
  )

  useLayoutEffect(() => {
    syncChartHistoryEditButtons(gridRef.current, editingChartId)
  }, [editingChartId, gridRef, historyData])

  useEffect(() => {
    if (!preferCreateDentalChart) {
      return
    }
    setShowEditConfirm(false)
    setChartToEdit(null)
  }, [preferCreateDentalChart])

  useEffect(() => {
    reconcilePendingDelete()
  }, [historyData.length, reconcilePendingDelete])

  useEffect(() => {
    if (!layoutSyncKey) {
      return undefined
    }
    const frameId = window.requestAnimationFrame(() => {
      if (suppressSelectionSyncRef.current) return
      const chartId = store.getState().charting.selectedDentalChart?.chart_id
      if (!chartId) return
      selectChartRowIfAvailable(chartId)
    })
    return (): void => {
      window.cancelAnimationFrame(frameId)
    }
  }, [
    layoutSyncKey,
    selectChartRowIfAvailable,
    store,
    suppressSelectionSyncRef,
  ])

  const handleDeleteChart = useCallback((chart: IDentalChart): void => {
    setShowDeleteConfirm(true)
    setChartToDelete(chart)
  }, [])

  const extractDentalChartProperties = useCallback(
    (chart: IDentalChart & Record<string, unknown>): IDentalChart => {
      return {
        chart_id: chart.chart_id,
        patient_id: chart.patient_id,
        provider_id: chart.provider_id,
        clinic_id: chart.clinic_id,
        title: chart.title,
        description: chart.description,
        patient: chart.patient,
        provider: chart.provider,
        dental_data: chart.dental_data,
        notes: chart.notes,
        date_time_created_at: chart.date_time_created_at,
        date_time_updated_at: chart.date_time_updated_at,
      }
    },
    [],
  )

  const handleEditChart = useCallback((chart: IDentalChart): void => {
    setShowEditConfirm(true)
    setChartToEdit({ ...chart })
  }, [])

  const handleViewSummary = useCallback((chart: IDentalChart): void => {
    setSummaryChartTitle(chart.title ?? 'chart')
    setDentalSummary(formatDentalDataSummary(chart.dental_data))
    setShowSummaryModal(true)
  }, [])

  const handleRowDoubleClick = useCallback(
    (args: RecordDoubleClickEventArgs): void => {
      const chart = args.rowData as
        | (IDentalChart & Record<string, unknown>)
        | undefined

      if (!chart) {
        return
      }

      const cleanChart = extractDentalChartProperties(chart)
      // Defer modal open until Syncfusion finishes DOM updates from the
      // double-click / selection cycle (avoids React removeChild errors).
      window.setTimeout(() => {
        if (!isMountedRef.current) {
          return
        }
        handleViewSummary(cleanChart)
      }, 0)
    },
    [extractDentalChartProperties, handleViewSummary],
  )

  /** Keep history selection aligned with create chart so the side panel exits view-only mode (see ChartingSidePanel `isViewMode`). */
  const confirmEditChart = useCallback(
    (chart: IDentalChart): void => {
      dispatch(setCreateDentalChart({ ...chart }))
      dispatch(setSelectedDentalChart({ ...chart }))
      dispatch(setPreferCreateDentalChart(false))
      dispatch(setDentalUpdated(false))
      setShowEditConfirm(false)

      const chartId = chart.chart_id
      if (!chartId || !gridRef.current) {
        return
      }
      selectChartRowIfAvailable(chartId)
    },
    [dispatch, gridRef, selectChartRowIfAvailable],
  )

  const columns: ColumnModel[] = useMemo(
    (): ColumnModel[] => [
      {
        field: 'date',
        headerText: 'Date',
        width: 80,
        template: (row: IDentalChart): JSX.Element => (
          <span>
            {new Date(row.date_time_created_at ?? '').toLocaleDateString()}
          </span>
        ),
      },
      {
        field: 'title',
        headerText: 'Chart name',
        width: 120,
      },
      {
        field: 'tooth_surface',
        headerText: 'Tooth/surface',
        width: 120,
      },
      {
        field: 'provider_name',
        headerText: 'Provider',
        width: 120,
      },
      {
        field: 'notes_col',
        headerText: 'Notes',
        headerTextAlign: 'Center',
        textAlign: 'Center',
        width: 50,
        allowSorting: false,
        template: (row: IDentalChart): JSX.Element => {
          return !!row.notes?.length ? (
            <button
              className="action-button action-button-view"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onViewNoteRef.current(row.chart_id, true)
              }}
            >
              <i className="mgc_document_fill" />
            </button>
          ) : (
            <span>-</span>
          )
        },
      },
      {
        field: 'actions_col',
        headerText: 'Actions',
        headerTextAlign: 'Center',
        textAlign: 'Center',
        width: 80,
        allowSorting: false,
        template: (row: IDentalChart): JSX.Element => (
          <div
            onClick={(e) => e.stopPropagation()}
            data-chart-history-id={row.chart_id ?? ''}
          >
            <ActionButtons
              showView={false}
              customMiddleButton={
                <Button
                  className="act-button"
                  title="View dental chart summary"
                  onClick={() => {
                    handleViewSummary(
                      extractDentalChartProperties(
                        row as IDentalChart & Record<string, unknown>,
                      ),
                    )
                  }}
                >
                  <i className="mgc mgc_information_fill" />
                </Button>
              }
              onEdit={() => {
                handleEditChart(
                  extractDentalChartProperties(
                    row as IDentalChart & Record<string, unknown>,
                  ),
                )
              }}
              onDelete={() => {
                handleDeleteChart(row)
              }}
              disableEdit={row.chart_id === editingChartIdRef.current}
            />
          </div>
        ),
      },
    ],
    [
      extractDentalChartProperties,
      handleDeleteChart,
      handleEditChart,
      handleViewSummary,
    ],
  )

  const showEmptyState = !chartingLoading && historyData.length === 0

  return (
    <div className="charting-history">
      {!showEmptyState && (
        <div className="charting-history-title mb-1">Charting history</div>
      )}
      <div className="history-grid-container">
        {showEmptyState ? (
          <EmptyState
            className="charting-history-empty"
            iconClass="mgc mgc_document_fill"
            title="No dental charts found"
            description={
              selectedPatient
                ? "You don't have any dental charts on record yet."
                : 'Select a patient to view their charts.'
            }
          />
        ) : (
          <DataGridComponent
            columns={columns}
            data={historyData}
            pageSize={10}
            selectionSettings={{
              type: 'Single',
              mode: 'Row',
              enableToggle: true,
            }}
            rowSelected={handleRowSelected}
            rowDeselected={handleRowDeselected}
            recordClick={handleRecordClick}
            allowPaging={true}
            ref={gridRef}
            customCSS="datatable-table charting-history-table"
            dataBound={handleHistoryDataBound}
            rowDataBound={handleRowDataBound}
            onActionComplete={handleGridActionComplete}
            onRowDoubleClick={handleRowDoubleClick}
            isLoading={false}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        )}

        <ConfirmationModal
          size="lg"
          show={showSummaryModal}
          onHide={() => setShowSummaryModal(false)}
          title={`Dental chart summary (${summaryChartTitle})`}
          body={
            dentalSummary ? (
              <DentalChartSummaryContent
                summary={dentalSummary}
                showHeading={false}
              />
            ) : (
              <div />
            )
          }
          footer={
            <Button
              size="sm"
              className="button-secondary"
              onClick={() => setShowSummaryModal(false)}
            >
              Close
            </Button>
          }
        />

        <ChartingDeleteItemModal
          show={showDeleteConfirm}
          itemName={chartToDelete?.title ?? ''}
          deleteDisabled={isDeleteInFlight}
          onCancel={() => setShowDeleteConfirm(false)}
          onDelete={() => {
            if (isDeleteInFlight) return

            const chartId = chartToDelete?.chart_id
            const chartTitle = chartToDelete?.title ?? 'chart'

            if (!chartId) {
              setShowDeleteConfirm(false)
              return
            }

            setPendingDeleteChartId(chartId)
            setPendingDeleteChartTitle(chartTitle)
            setIsDeleteInFlight(true)
            hasHandledPendingDeleteRef.current = false

            dispatch(deleteDentalChart(chartId))
              .unwrap()
              .then(() => {
                setIsDeleteInFlight(false)
              })
              .catch((error) => {
                clearPendingDeleteState()
                showToast(
                  createFailedToDeleteChartToast(
                    error instanceof Error ? error.message : undefined,
                  ),
                )
              })

            setShowDeleteConfirm(false)
          }}
        />

        {isDentalUpdated && chartToEdit ? (
          <ChartingDiscardInProgressModal
            show={showEditConfirm}
            chartName={chartToEdit.title ?? ''}
            onKeepEditing={() => setShowEditConfirm(false)}
            onDiscard={() => {
              if (!chartToEdit) return
              confirmEditChart(chartToEdit)
            }}
          />
        ) : (
          <ChartingEditChartModal
            show={showEditConfirm}
            chartName={chartToEdit?.title ?? ''}
            actionVerb="update"
            onCancel={() => setShowEditConfirm(false)}
            onContinue={() => {
              if (!chartToEdit) return
              confirmEditChart(chartToEdit)
            }}
          />
        )}
      </div>
    </div>
  )
}

export default React.memo(ChartingHistory)
